/**
 * @author Harish.R <harish.r@314ecorp.com>
 * @description Access Control component
 */

import React from 'react';
import _ from 'lodash';
import type { FeatureConfig } from '@veritable/common/adminApi';
import { useAppStoreValues } from 'store/app';
import { NO_ACCESS_TOOLTIP_TEXT } from '@veritable/common/constants';

type BooleanPaths<T, Prefix extends string = ''> = T extends object
	? {
			[K in Extract<keyof T, string>]: T[K] extends boolean ? `${Prefix}${K}` : BooleanPaths<T[K], `${Prefix}${K}.`>;
		}[Extract<keyof T, string>]
	: never;

const AccessControl: React.FC<{
	children: React.ReactElement;
	permission?: keyof App.UserAccess;
	feature?: FeatureFlag;
	disableOnly?: boolean;
}> = ({ children, permission, feature, disableOnly }) => {
	const { userAccess, features } = useAppStoreValues();

	if (feature && !_.get(features, feature)) {
		return null;
	}

	const hasAccess = permission ? userAccess[permission] || false : true;

	if (disableOnly && !hasAccess) {
		return React.cloneElement(children, { disabled: true, title: NO_ACCESS_TOOLTIP_TEXT });
	}

	return hasAccess ? <>{children}</> : null;
};

const useFilterSchema = <T, >(schema: T[]): T[] => {
	const { userAccess, features } = useAppStoreValues();

	return schema?.filter((item) => {
		const feature = (item as AnyType)?.feature as FeatureFlag | undefined;
		if (feature && !_.get(features, feature)) {
			return false;
		}

		const permissions = (item as AnyType)?.permissions as keyof App.UserAccess | (keyof App.UserAccess)[] | undefined;
		if (!permissions) {
			return true;
		}

		const permissionsArray = Array.isArray(permissions) ? permissions : [permissions];
		return permissionsArray.every((permission) => userAccess?.[permission] === true);
	});
};

export type FeatureFlag = BooleanPaths<FeatureConfig>;
export default AccessControl;
export { useFilterSchema };
